import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import PageWrapper from "../../components/layout/PageWrapper";
import employeeRequestService from "../../api/employeeRequestService";
import useInfiniteList from "../../hooks/useInfiniteList";
import { ErrorState, SkeletonRows } from "../../components/task-board/shared";
import { REQUESTS_CHANGED_EVENT, STATUS_COLOR } from "../../components/employee-requests/constants";
import RequestPanel from "../../components/employee-requests/RequestPanel";
import RequestsTable from "../../components/employee-requests/RequestsTable";
import RequestsToolbar from "../../components/employee-requests/RequestsToolbar";
import "../../components/employee-requests/employeeRequests.css";

// Server page size; the API caps it at 100.
const PAGE_SIZE = 100;
const EMPTY = { type: "", status: "", severity: "", unread: "", q: "" };

const EmployeeRequests = () => {
  const { auth } = useAuth();
  const canAct = isAdmin(auth?.employee); // non-admins: view only, no unread tracking
  const [filters, setFilters] = useState(EMPTY);
  const [openId, setOpenId] = useState(null);

  // Infinite scroll: pages of PAGE_SIZE are appended as you reach the bottom.
  const { items: requests, total, extra, loading, loadingMore, error, hasMore, reload, retry, sentinelRef, loadMore } = useInfiniteList(
    (page) => employeeRequestService.list({ ...filters, q: filters.q.trim(), page, limit: PAGE_SIZE }),
    JSON.stringify(filters),
  );

  // Deep link (Discord / notification): ?request=<id> opens that request, once.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("request");
    if (!id) return;
    setOpenId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("request");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Live updates: refetch (debounced) whenever the server reports a change.
  useEffect(() => {
    let timer;
    const onChange = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 300);
    };
    window.addEventListener(REQUESTS_CHANGED_EVENT, onChange);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(REQUESTS_CHANGED_EVENT, onChange);
    };
  }, [reload]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const hasFilters = Object.values(filters).some(Boolean);

  const stats = extra.stats || { types: {}, statuses: {}, total: 0 };
  const unread = extra.unreadCount || 0;
  const chips = [
    { label: "Open", n: stats.statuses.open || 0, c: STATUS_COLOR.open },
    { label: "In progress", n: stats.statuses.in_progress || 0, c: STATUS_COLOR.in_progress },
    // "Resolved" also counts closed (rejected) requests.
    { label: "Resolved", n: (stats.statuses.resolved || 0) + (stats.statuses.rejected || 0), c: STATUS_COLOR.resolved },
  ];
  const counts = { all: stats.total || 0, ...stats.types };

  return (
    <PageWrapper>
      <div className="er-page">
        <div className="er-head">
          <div>
            <div className="er-row" style={{ gap: 12 }}>
              <h1 className="er-h1">Employee Requests</h1>
              {canAct && unread > 0 && <span className="er-newpill"><i />{unread} new</span>}
            </div>
            <div style={{ marginTop: 6, color: "var(--muted)" }}>
              Issues, feature requests and suggestions submitted from the employee portal.{canAct ? "" : " You have view-only access."}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {chips.map((s) => (
              <div className="er-stat" key={s.label}>
                <i style={{ background: s.c, boxShadow: `0 0 8px ${s.c}` }} />
                <b>{s.n}</b>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <RequestsToolbar filters={filters} setFilter={setFilter} counts={counts} showUnread={canAct} />

        {error ? (
          <ErrorState message={error} onRetry={retry} />
        ) : loading && requests.length === 0 ? (
          <SkeletonRows rows={5} />
        ) : (
          <RequestsTable
            requests={requests}
            total={total}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            loadMore={loadMore}
            sentinelRef={sentinelRef}
            hasFilters={hasFilters}
            onClear={() => setFilters(EMPTY)}
            onOpen={setOpenId}
            showUnread={canAct}
          />
        )}
      </div>

      {openId && <RequestPanel key={openId} requestId={openId} onClosed={() => setOpenId(null)} />}
    </PageWrapper>
  );
};

export default EmployeeRequests;
